import type { SupabaseClientConfig } from './config';

type SupabaseCountPreference = 'exact' | 'planned' | 'estimated';

interface SupabaseSelectOptions {
  columns?: string;
  filters?: Record<string, SupabaseFilterValue>;
  limit?: number;
  offset?: number;
  order?: {
    column: string;
    ascending?: boolean;
    nullsFirst?: boolean;
    foreignTable?: string;
  };
  prefer?: string | string[];
  count?: SupabaseCountPreference;
  signal?: AbortSignal;
}

interface SupabaseUpsertOptions {
  onConflict?: string;
  mergeDuplicates?: boolean;
  prefer?: string | string[];
  count?: SupabaseCountPreference;
  returning?: 'representation' | 'minimal';
  signal?: AbortSignal;
  headers?: HeadersInit;
}

interface SupabaseMutationOptions {
  filters?: Record<string, SupabaseFilterValue>;
  prefer?: string | string[];
  count?: SupabaseCountPreference;
  returning?: 'representation' | 'minimal';
  signal?: AbortSignal;
  headers?: HeadersInit;
}

type SupabaseFilterValue = string | number | boolean | null | Array<string | number>;

class SupabaseRequestError extends Error {
  readonly status: number;
  readonly url: string;
  readonly details: unknown;

  constructor(status: number, url: string, details: unknown) {
    super(
      `Supabase request failed with status ${status}${
        typeof details === 'string' ? `: ${details}` : ''
      }`
    );
    this.name = 'SupabaseRequestError';
    this.status = status;
    this.url = url;
    this.details = details;
  }
}

export class SupabaseRestClient {
  private readonly baseUrl: string;
  private readonly baseHeaders: HeadersInit;

  constructor(config: SupabaseClientConfig) {
    const { url, apiKey, schema, accessToken } = config;
    this.baseUrl = `${url}/rest/v1/`;
    // Use user's JWT access token if available, otherwise fall back to API key
    const bearerToken = accessToken ?? apiKey;
    this.baseHeaders = {
      apikey: apiKey,
      Authorization: `Bearer ${bearerToken}`,
      Accept: 'application/json',
    };

    if (schema && schema !== 'public') {
      this.baseHeaders['Accept-Profile'] = schema;
      this.baseHeaders['Content-Profile'] = schema;
    }
  }

  async select<T = unknown>(resource: string, options: SupabaseSelectOptions = {}): Promise<T[]> {
    const searchParams = new URLSearchParams();
    searchParams.set('select', options.columns ?? '*');

    if (options.limit !== undefined) {
      searchParams.set('limit', String(options.limit));
    }

    if (options.offset !== undefined) {
      searchParams.set('offset', String(options.offset));
    }

    if (options.filters) {
      for (const [column, value] of Object.entries(options.filters)) {
        searchParams.set(column, encodeFilterValue(value));
      }
    }

    if (options.order) {
      const direction = options.order.ascending === false ? 'desc' : 'asc';
      const nulls = options.order.nullsFirst ? '.nullsfirst' : '.nullslast';
      const foreignPrefix = options.order.foreignTable ? `${options.order.foreignTable}.` : '';
      searchParams.set('order', `${foreignPrefix}${options.order.column}.${direction}${nulls}`);
    }

    const preferBase = normalizePreferInput(options.prefer);
    const prefer = mergePreferDirectives(
      preferBase,
      options.count ? [`count=${options.count}`] : undefined
    );

    const response = await this.request<T[]>(resource, {
      method: 'GET',
      searchParams,
      signal: options.signal,
      headers: prefer.size > 0 ? { Prefer: Array.from(prefer).join(', ') } : undefined,
    });

    return response ?? [];
  }

  async upsert<T = unknown>(
    resource: string,
    payload: unknown,
    options: SupabaseUpsertOptions = {}
  ): Promise<T[]> {
    const searchParams = new URLSearchParams();

    if (options.onConflict) {
      searchParams.set('on_conflict', options.onConflict);
    }

    const preferDirectives: string[] = [];
    if (options.mergeDuplicates !== false) {
      preferDirectives.push('resolution=merge-duplicates');
    }

    const preferInput = normalizePreferInput(options.prefer);
    if (!hasDirective(preferInput, (entry) => entry.startsWith('return='))) {
      preferDirectives.push(`return=${options.returning ?? 'representation'}`);
    }

    if (options.count) {
      preferDirectives.push(`count=${options.count}`);
    }

    const prefer = mergePreferDirectives(preferInput, preferDirectives);

    const response = await this.request<T[]>(resource, {
      method: 'POST',
      searchParams,
      body: payload,
      signal: options.signal,
      headers: {
        'Content-Type': 'application/json',
        ...(options.headers ?? {}),
        ...(prefer.size > 0 ? { Prefer: Array.from(prefer).join(', ') } : {}),
      },
    });

    return response ?? [];
  }

  async update<T = unknown>(
    resource: string,
    payload: unknown,
    options: SupabaseMutationOptions = {}
  ): Promise<T[]> {
    const searchParams = new URLSearchParams();

    if (options.filters) {
      for (const [column, value] of Object.entries(options.filters)) {
        searchParams.set(column, encodeFilterValue(value));
      }
    }

    const preferDirectives: string[] = [];
    const preferInput = normalizePreferInput(options.prefer);
    if (!hasDirective(preferInput, (entry) => entry.startsWith('return='))) {
      preferDirectives.push(`return=${options.returning ?? 'representation'}`);
    }

    if (options.count) {
      preferDirectives.push(`count=${options.count}`);
    }

    const prefer = mergePreferDirectives(preferInput, preferDirectives);

    const response = await this.request<T[]>(resource, {
      method: 'PATCH',
      searchParams,
      body: payload,
      signal: options.signal,
      headers: {
        'Content-Type': 'application/json',
        ...(options.headers ?? {}),
        ...(prefer.size > 0 ? { Prefer: Array.from(prefer).join(', ') } : {}),
      },
    });

    return response ?? [];
  }

  async delete<T = unknown>(resource: string, options: SupabaseMutationOptions = {}): Promise<T[]> {
    const searchParams = new URLSearchParams();

    if (options.filters) {
      for (const [column, value] of Object.entries(options.filters)) {
        searchParams.set(column, encodeFilterValue(value));
      }
    }

    const preferDirectives: string[] = [];
    const preferInput = normalizePreferInput(options.prefer);
    if (!hasDirective(preferInput, (entry) => entry.startsWith('return='))) {
      preferDirectives.push(`return=${options.returning ?? 'representation'}`);
    }

    if (options.count) {
      preferDirectives.push(`count=${options.count}`);
    }

    const prefer = mergePreferDirectives(preferInput, preferDirectives);

    const response = await this.request<T[]>(resource, {
      method: 'DELETE',
      searchParams,
      signal: options.signal,
      headers: prefer.size > 0 ? { Prefer: Array.from(prefer).join(', ') } : undefined,
    });

    return response ?? [];
  }

  private async request<T>(
    resource: string,
    init: {
      method: string;
      searchParams?: URLSearchParams;
      body?: unknown;
      headers?: HeadersInit;
      signal?: AbortSignal;
    }
  ): Promise<T> {
    const url = this.buildResourceUrl(resource, init.searchParams);

    const headers: HeadersInit = {
      ...this.baseHeaders,
      ...init.headers,
    };

    const body =
      init.body === undefined
        ? undefined
        : typeof init.body === 'string'
          ? init.body
          : JSON.stringify(init.body);

    const response = await fetch(url, {
      method: init.method,
      headers,
      body,
      signal: init.signal,
    });

    if (!response.ok) {
      const details = await safelyParseError(response);
      throw new SupabaseRequestError(response.status, url, details);
    }

    if (response.status === 204) {
      return undefined as T;
    }

    const contentType = response.headers.get('content-type') ?? '';
    if (contentType.includes('application/json')) {
      return (await response.json()) as T;
    }

    return (await response.text()) as T;
  }

  private buildResourceUrl(resource: string, searchParams?: URLSearchParams): string {
    const normalized = resource.replace(/^\/+/, '');
    const url = new URL(normalized, this.baseUrl);

    if (searchParams) {
      searchParams.forEach((value, key) => {
        url.searchParams.append(key, value);
      });
    }

    return url.toString();
  }
}

function encodeFilterValue(value: SupabaseFilterValue): string {
  if (Array.isArray(value)) {
    const serialized = value.map((item) => serializeFilterOperand(item));
    return `in.(${serialized.join(',')})`;
  }

  if (value === null) {
    return 'is.null';
  }

  if (typeof value === 'string' && value.includes('.')) {
    const operator = value.split('.')[0];
    if (KNOWN_OPERATORS.has(operator)) {
      return value;
    }
  }

  return `eq.${serializeFilterOperand(value)}`;
}

function serializeFilterOperand(value: string | number | boolean): string {
  if (typeof value === 'boolean') {
    return value ? 'true' : 'false';
  }
  return String(value);
}

function mergePreferDirectives(base: string[] | undefined, extra?: string | string[]): Set<string> {
  const directives = new Set(base ?? []);

  const add = (fragment?: string | string[]) => {
    if (!fragment) return;
    const entries = normalizePreferInput(fragment);
    entries.forEach((entry) => directives.add(entry));
  };

  add(extra);

  return directives;
}

async function safelyParseError(response: Response): Promise<unknown> {
  const contentType = response.headers.get('content-type') ?? '';
  if (contentType.includes('application/json')) {
    try {
      return await response.json();
    } catch (error) {
      return { parseError: String(error) };
    }
  }

  try {
    return await response.text();
  } catch (error) {
    return { parseError: String(error) };
  }
}

const KNOWN_OPERATORS = new Set([
  'eq',
  'neq',
  'gt',
  'gte',
  'lt',
  'lte',
  'like',
  'ilike',
  'is',
  'in',
  'cs',
  'cd',
  'ov',
  'sl',
  'sr',
  'nxr',
  'nxl',
  'adj',
  'not',
]);

function normalizePreferInput(input?: string | string[]): string[] {
  if (!input) return [];
  const raw = Array.isArray(input) ? input : [input];
  return raw
    .flatMap((entry) => entry.split(','))
    .map((entry) => entry.trim())
    .filter((entry) => entry.length > 0);
}

function hasDirective(
  directives: string[] | Set<string>,
  predicate: (entry: string) => boolean
): boolean {
  const entries = directives instanceof Set ? Array.from(directives) : directives;
  return entries.some(predicate);
}
